import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash2, Edit, Plus, GripVertical } from "lucide-react";
import { PageVisibilityToggle } from "@/components/ui/PageVisibilityToggle";
import { useToast } from "@/hooks/use-toast";
import { faqsApi, type Faq } from "@/integrations/mysql/client";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface SortableFAQItemProps {
  faq: Faq;
  onEdit: (faq: Faq) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string, isActive: boolean) => void;
}

function SortableFAQItem({ faq, onEdit, onDelete, onToggleActive }: SortableFAQItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: faq.id });
  return (
    <Card ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing mt-1 shrink-0">
            <GripVertical className="w-5 h-5 text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-1.5">
              <h4 className="font-semibold text-sm leading-snug flex-1 min-w-0">{faq.question}</h4>
              <Badge variant={faq.is_active ? "default" : "secondary"} className="shrink-0 text-xs">
                {faq.is_active ? "Ativo" : "Inativo"}
              </Badge>
            </div>
            <p className="text-muted-foreground text-xs line-clamp-2 mb-3">{faq.answer}</p>
            <div className="flex items-center gap-2">
              <Switch checked={!!faq.is_active} onCheckedChange={(v) => onToggleActive(faq.id, v)} />
              <Button size="sm" variant="outline" onClick={() => onEdit(faq)} className="h-7 w-7 p-0">
                <Edit className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => onDelete(faq.id)} className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:border-red-300">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function FAQManager() {
  const [faqs, setFaqs] = useState<Faq[]>([]);
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ question: "", answer: "" });
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const { toast } = useToast();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const loadFaqs = async () => {
    try {
      setFaqs(await faqsApi.list(true));
    } catch {
      toast({ title: "Erro", description: "Erro ao carregar FAQs", variant: "destructive" });
    }
  };

  useEffect(() => { loadFaqs(); }, []);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = faqs.findIndex(f => f.id === active.id);
    const newIndex = faqs.findIndex(f => f.id === over.id);
    const reordered = arrayMove(faqs, oldIndex, newIndex);
    setFaqs(reordered);
    try {
      await Promise.all(reordered.map((faq, i) => faqsApi.update(faq.id, { display_order: i })));
      toast({ title: "Ordem atualizada com sucesso!" });
    } catch {
      toast({ title: "Erro", description: "Erro ao atualizar ordem", variant: "destructive" });
      loadFaqs();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const q = formData.question.trim().slice(0, 500);
    const a = formData.answer.trim().slice(0, 2000);
    if (q.length < 10 || a.length < 20) {
      toast({ title: "Erro", description: "Pergunta (mín. 10) e resposta (mín. 20) são obrigatórias.", variant: "destructive" });
      return;
    }
    try {
      if (isEditing) {
        await faqsApi.update(isEditing, { question: q, answer: a });
        toast({ title: "FAQ atualizada com sucesso!" });
      } else {
        await faqsApi.create({ question: q, answer: a, display_order: faqs.length, is_active: true });
        toast({ title: "FAQ criada com sucesso!" });
      }
      setFormData({ question: "", answer: "" });
      setIsEditing(null);
      setShowForm(false);
      loadFaqs();
    } catch {
      toast({ title: "Erro", description: "Erro ao salvar FAQ", variant: "destructive" });
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await faqsApi.delete(deleteId);
      toast({ title: "FAQ excluída com sucesso!" });
      loadFaqs();
    } catch {
      toast({ title: "Erro", description: "Erro ao excluir FAQ", variant: "destructive" });
    } finally {
      setDeleteId(null);
    }
  };

  const handleToggleActive = async (id: string, isActive: boolean) => {
    try {
      await faqsApi.update(id, { is_active: isActive });
      toast({ title: isActive ? "FAQ ativada!" : "FAQ desativada!" });
      loadFaqs();
    } catch {
      toast({ title: "Erro", description: "Erro ao atualizar status", variant: "destructive" });
    }
  };

  const isFormValid = formData.question.trim().length >= 10 && formData.answer.trim().length >= 20;

  return (
    <div className="space-y-6">
      <PageVisibilityToggle pageKey="page_faq_visible" label="Seção de Perguntas Frequentes" />

      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold"></h3>
        {!showForm && (
          <Button onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" /> Adicionar FAQ
          </Button>
        )}
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{isEditing ? "Editar" : "Nova"} FAQ</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Pergunta <span className="text-muted-foreground">({formData.question.length}/500)</span>
                </label>
                <Input
                  value={formData.question}
                  onChange={e => setFormData(p => ({ ...p, question: e.target.value }))}
                  placeholder="Digite a pergunta (mínimo 10 caracteres)"
                  maxLength={500}
                  required
                />
                {formData.question.trim().length > 0 && formData.question.trim().length < 10 && (
                  <p className="text-sm text-destructive mt-1">Mínimo 10 caracteres</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Resposta <span className="text-muted-foreground">({formData.answer.length}/2000)</span>
                </label>
                <Textarea
                  value={formData.answer}
                  onChange={e => setFormData(p => ({ ...p, answer: e.target.value }))}
                  placeholder="Digite a resposta (mínimo 20 caracteres)"
                  rows={4}
                  maxLength={2000}
                  required
                />
                {formData.answer.trim().length > 0 && formData.answer.trim().length < 20 && (
                  <p className="text-sm text-destructive mt-1">Mínimo 20 caracteres</p>
                )}
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={!isFormValid}>{isEditing ? "Atualizar" : "Criar"}</Button>
                <Button type="button" variant="outline" onClick={() => { setShowForm(false); setIsEditing(null); setFormData({ question: "", answer: "" }); }}>
                  Cancelar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {faqs.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              Nenhuma FAQ encontrada. Adicione a primeira!
            </CardContent>
          </Card>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={faqs.map(f => f.id)} strategy={verticalListSortingStrategy}>
              {faqs.map(faq => (
                <SortableFAQItem
                  key={faq.id}
                  faq={faq}
                  onEdit={f => { setFormData({ question: f.question, answer: f.answer }); setIsEditing(f.id); setShowForm(true); }}
                  onDelete={id => setDeleteId(id)}
                  onToggleActive={handleToggleActive}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta FAQ? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
